import type * as EOT from '@base/server/services/exchange-office/@types'
import type { ExchangeOfficeClientVerificationResult } from '@base/types'

export default function useExchangeOffice () {
  const { isAdmin } = storeToRefs(useUser())

  const fetchExchangeOffice = async (params: EOT.GetExchangeOfficeInput) => {
    let url: string

    if (params.userEmail) {
      url = `/api/admin/exchange-offices/users/${params.userEmail}`
    }
    else if (isAdmin.value) {
      url = `/api/admin/exchange-offices/${params.id}`
    }
    else {
      url = `/api/exchange-offices/${params.id}`
    }

    return await $superFetch<EOT.GetExchangeOfficeOutput>(url, {
      headers: useRequestHeaders(['cookie']),
      query: params,
    })
  }

  const fetchExchangeOffices = async (params?: EOT.GetExchangeOfficesInput) => {
    const url = '/api/admin/exchange-offices'

    return await $superFetch<EOT.GetExchangeOfficesOutput>(url, {
      headers: useRequestHeaders(['cookie']),
      query: params,
    })
  }

  const fetchExchangeOfficeBranch = async (params: EOT.GetExchangeOfficeBranchInput) => {
    const url = isAdmin.value ? `/api/admin/exchange-offices/branches/${params.branchId}` : `/api/exchange-offices/branches/${params.branchId}`

    return await $superFetch<EOT.GetExchangeOfficeBranchOutput>(url, {
      headers: useRequestHeaders(['cookie']),
      query: params,
    })
  }

  const fetchExchangeOfficeBranches = async (params: EOT.GetExchangeOfficeBranchesInput) => {
    const url = isAdmin.value ? `/api/admin/exchange-offices/branches` : `/api/exchange-offices/branches`

    return await $superFetch<EOT.GetExchangeOfficeBranchesOutput>(url, {
      headers: useRequestHeaders(['cookie']),
      query: params,
    })
  }

  const fetchExchangeOfficeOverview = async (params: EOT.GetExchangeOfficeOverviewAdminInput) => {
    const url = `/api/admin/exchange-offices/${params.exchangeOfficeId}/overview`

    return await $superFetch<EOT.GetExchangeOfficeOverviewAdminOutput>(url, {
      headers: useRequestHeaders(['cookie']),
      query: params,
    })
  }

  const fetchExchangeOfficeMaxBranches = async (params: EOT.GetExchangeOfficeMaxBranchesInput) => {
    return await $superFetch<EOT.GetExchangeOfficeMaxBranchesOutput>(`/api/admin/exchange-offices/${params.exchangeOfficeId}/max-branches`, {
      headers: useRequestHeaders(['cookie']),
    })
  }

  const updateExchangeOffice = async (id: number, data: EOT.UpdateExchangeOfficeInput) => {
    const url = isAdmin.value ? `/api/admin/exchange-offices/${id}` : `/api/exchange-offices/${id}`

    return await $superFetch<EOT.UpdateExchangeOfficeOutput>(url, {
      method: 'PATCH',
      body: data,
    })
  }

  const updateExchangeOfficeBranch = async (params: EOT.UpdateExchangeOfficeBranchInput) => {
    const url = isAdmin.value ? `/api/admin/exchange-offices/branches/${params.id}` : `/api/exchange-offices/branches/${params.id}`

    return await $superFetch<EOT.UpdateExchangeOfficeBranchOutput>(url, {
      method: 'PATCH',
      body: params,
    })
  }

  const createExchangeOfficeBranch = async (data: EOT.CreateExchangeOfficeBranchInput) => {
    const url = isAdmin.value ? '/api/admin/exchange-offices/branches' : '/api/exchange-offices/branches'

    return await $superFetch<EOT.CreateExchangeOfficeBranchOutput>(url, {
      method: 'POST',
      body: data,
    })
  }

  const fetchExchangeOfficeCharges = async (params: EOT.GetExchangeOfficeChargesInput) => {
    if (isAdmin.value)
      if (!params.exchangeOfficeId)
        throw createError({
          statusCode: 400,
          statusMessage: 'Admin didnt provide ExchangeOfficeId',
        })

    const url = isAdmin.value ? '/api/admin/exchange-offices/charges' : '/api/exchange-offices/charges'
    return await $superFetch<EOT.GetExchangeOfficeChargesOutput>(url, {
      headers: useRequestHeaders(['cookie']),
      query: isAdmin.value ? { exchangeOfficeId: params.exchangeOfficeId } : {},
    })
  }

  const calculateExchangeOfficeCredit = async (exchangeOfficeId?: number) => {
    const url = isAdmin.value ? '/api/admin/exchange-offices/calculate-credit' : '/api/exchange-offices/calculate-credit'

    return await $superFetch<EOT.CalculateExchangeOfficeCreditOutput>(url, {
      headers: useRequestHeaders(['cookie']),
      query: isAdmin.value ? { exchangeOfficeId } : {},
    })
  }

  const fetchExchangeOfficeClientVerificationStatus = async (params: EOT.GetExchangeOfficeClientVerificationInput) => {
    return await $superFetch<ExchangeOfficeClientVerificationResult>(`/api/exchange-offices/verifications/${params.clientId}`, {
      headers: useRequestHeaders(['cookie']),
      query: { type: params.type },
    })
  }

  const fetchExchangeOfficeEmployees = async (params?: EOT.GetExchangeOfficeEmployeesInput) => {
    const url = isAdmin.value ? '/api/admin/exchange-offices/employees' : '/api/exchange-offices/employees'

    const employees = await $superFetch<EOT.GetExchangeOfficeEmployeesOutput>(url, {
      headers: useRequestHeaders(['cookie']),
      query: params,
    })
    return employees
  }

  const fetchExchangeOfficeEmployeesUnregistered = async (params?: EOT.GetExchangeOfficeEmployeesUnregisteredInput) => {
    const url = isAdmin.value ? '/api/admin/exchange-offices/employees/unregistered' : '/api/exchange-offices/employees/unregistered'

    return await $superFetch<EOT.GetExchangeOfficeEmployeesUnregisteredOutput>(url, {
      headers: useRequestHeaders(['cookie']),
      query: params,
    })
  }

  const registerExchangeOfficeEmployee = async (credentials: { email: string, password: string }, metadata?: Record<string, any>) => {
    return await $superFetch<{ user: { id: string, email: string }, properties: { action_link: string } }>('/api/exchange-offices/employees/register', {
      method: 'POST',
      headers: useRequestHeaders(['cookie']),
      body: {
        email: credentials.email,
        password: credentials.password,
        redirectTo: `${useRequestURL().origin}/email-verified`,
        metadata,
      },
    })
  }

  const updateExchangeOfficeCashDesk = async (cashDeskId: number, data: any) => {
    return await $superFetch<EOT.UpdateExchangeOfficeCashDeskOutput>(`/api/exchange-offices/cash-desks/${cashDeskId}`, {
      method: 'PATCH',
      body: data,
    })
  }

  const archiveExchangeOfficeCashDesk = async (cashDeskId: number) => {
    return await $superFetch<EOT.UpdateExchangeOfficeCashDeskOutput>(`/api/exchange-offices/cash-desks/${cashDeskId}`, {
      method: 'PATCH',
      body: {
        archived: true,
      },
    })
  }

  return {
    fetchExchangeOffice,
    fetchExchangeOffices,
    fetchExchangeOfficeBranch,
    fetchExchangeOfficeBranches,
    fetchExchangeOfficeOverview,
    fetchExchangeOfficeMaxBranches,
    updateExchangeOffice,
    updateExchangeOfficeBranch,
    createExchangeOfficeBranch,
    fetchExchangeOfficeCharges,
    calculateExchangeOfficeCredit,
    fetchExchangeOfficeEmployees,
    fetchExchangeOfficeEmployeesUnregistered,
    registerExchangeOfficeEmployee,
    updateExchangeOfficeCashDesk,
    archiveExchangeOfficeCashDesk,
    fetchExchangeOfficeClientVerificationStatus,
  }
}
